import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Pill from '../components/Pill';
import { fetchHof, voteExcuse, fetchStats } from '../lib/api';
import type { HofCard, Stats } from '../lib/api';

const TIME_FILTERS = [
  { label: 'All time', value: 'all' as const },
  { label: 'This week', value: 'week' as const },
  { label: 'Today', value: 'today' as const },
];

const CAT_FILTERS = ['all', 'Work', 'Social', 'Family', 'Dating'];

export default function HallOfFame() {
  const [time, setTime] = useState<'all' | 'week' | 'today'>('all');
  const [category, setCategory] = useState<string>('all');
  const [featured, setFeatured] = useState<HofCard | null>(null);
  const [cards, setCards] = useState<HofCard[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [voted, setVoted] = useState<Set<string>>(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem('voted') ?? '[]');
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchHof({ time, category })
      .then((res) => {
        if (cancelled) return;
        setFeatured(res.featured);
        setCards(res.cards);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load the Hall of Fame. Try again in a moment.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [time, category]);

  useEffect(() => {
    fetchStats()
      .then(setStats)
      .catch(() => {
        /* ignore */
      });
  }, []);

  const vote = async (id: string) => {
    if (voted.has(id)) return;
    try {
      const result = await voteExcuse(id);
      const next = new Set(voted);
      next.add(id);
      setVoted(next);
      localStorage.setItem('voted', JSON.stringify([...next]));
      setCards((prev) =>
        prev.map((c) => (c.id === id ? { ...c, votes: result.votes } : c))
      );
      setFeatured((prev) =>
        prev && prev.id === id ? { ...prev, votes: result.votes } : prev
      );
    } catch {
      /* silent — user just taps again */
    }
  };

  return (
    <div>
      <section className="pad-x" style={{ padding: '64px 40px 48px' }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.14em',
            color: 'var(--tag)',
            marginBottom: 20,
            textTransform: 'uppercase',
          }}
        >
          Community Picks
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 40,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 260 }}>
            <h1
              className="hero-title"
              style={{
                fontSize: 52,
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: '-0.03em',
                marginBottom: 20,
              }}
            >
              The greatest excuses
              <br />
              ever generated.
            </h1>
            <p
              style={{
                fontSize: 14,
                lineHeight: 1.65,
                color: 'var(--fg-muted)',
                maxWidth: 320,
                textWrap: 'pretty',
              }}
            >
              Real users. Real situations. Anonymous, obviously. Voted up by
              people who wish they'd thought of it first.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap' }}>
            <Stat label="excuses submitted" value={stats?.excuses_submitted} />
            <Stat label="obligations dodged" value={stats?.obligations_dodged} />
          </div>
        </div>
      </section>

      <div
        className="pad-x"
        style={{ padding: '0 40px 32px', borderBottom: '1px solid var(--border)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 11,
              color: 'var(--tag)',
              letterSpacing: '0.06em',
              marginRight: 4,
            }}
          >
            Filter by
          </span>
          {TIME_FILTERS.map((f) => (
            <Pill
              key={f.value}
              label={f.label}
              active={time === f.value}
              onClick={() => setTime(f.value)}
            />
          ))}
          <div
            style={{
              width: 1,
              height: 20,
              background: 'var(--border)',
              margin: '0 6px',
            }}
          />
          {CAT_FILTERS.map((f) => (
            <Pill
              key={f}
              label={f === 'all' ? 'All categories' : f}
              active={category === f}
              onClick={() => setCategory(f)}
            />
          ))}
        </div>
      </div>

      <div className="pad-x" style={{ padding: '40px 40px 80px' }}>
        {error && (
          <div
            style={{
              background: '#2a1a1a',
              border: '1px solid #5c2a2a',
              color: '#e29090',
              padding: '14px 18px',
              borderRadius: 6,
              fontSize: 13,
              marginBottom: 20,
            }}
          >
            {error}
          </div>
        )}

        {loading && cards.length === 0 && !featured ? (
          <LoadingGrid />
        ) : (
          <>
            {featured && (
              <FeaturedCard
                card={featured}
                voted={voted.has(featured.id)}
                onVote={() => vote(featured.id)}
              />
            )}

            <HofGrid cards={cards} voted={voted} onVote={vote} />

            {!loading && cards.length === 0 && !featured && (
              <div
                style={{
                  padding: '60px 24px',
                  textAlign: 'center',
                  color: 'var(--fg-muted)',
                  fontSize: 14,
                  border: '1px dashed var(--border)',
                  borderRadius: 8,
                }}
              >
                Nothing here yet for this filter. Be the first.
              </div>
            )}
          </>
        )}
      </div>

      <footer
        className="pad-x"
        style={{
          borderTop: '1px solid var(--border)',
          padding: '20px 40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--tag)' }}>
          Generated an excuse worth immortalising? Submit it anonymously.
        </span>
        <Link
          to="/submit"
          style={{
            padding: '10px 20px',
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 500,
            color: '#b8b4af',
            textDecoration: 'none',
          }}
        >
          Submit an excuse →
        </Link>
      </footer>
    </div>
  );
}

function Stat({ label, value }: { label: string; value?: number }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div
        style={{
          fontSize: 40,
          fontWeight: 700,
          letterSpacing: '-0.04em',
          lineHeight: 1,
        }}
      >
        {value?.toLocaleString() ?? '—'}
      </div>
      <div style={{ fontSize: 12, color: 'var(--tag)', marginTop: 6 }}>{label}</div>
    </div>
  );
}

function FeaturedCard({
  card,
  voted,
  onVote,
}: {
  card: HofCard;
  voted: boolean;
  onVote: () => void;
}) {
  return (
    <div
      style={{
        background: 'var(--card-bg)',
        border: '1px solid #3a3836',
        borderRadius: 8,
        padding: '28px 28px 24px',
        marginBottom: 24,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 18,
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.12em',
            color: 'var(--tag)',
            textTransform: 'uppercase',
          }}
        >
          {card.type} — Top pick
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              background: '#3d2e0a',
              color: '#d4a42a',
              fontSize: 11,
              fontWeight: 600,
              padding: '4px 10px',
              borderRadius: 4,
              letterSpacing: '0.04em',
            }}
          >
            Gold excuse
          </span>
          <VoteButton votes={card.votes} voted={voted} onVote={onVote} big />
        </div>
      </div>
      <p
        style={{
          fontSize: 16,
          lineHeight: 1.65,
          color: 'var(--fg)',
          textWrap: 'pretty',
          marginBottom: 20,
        }}
      >
        "{card.text}"
      </p>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12, color: 'var(--fg-dim)' }}>
          submitted anonymously · {card.category.toLowerCase()}
        </span>
        <span
          style={{
            fontSize: 11,
            color: '#888',
            border: '1px solid #333',
            borderRadius: 4,
            padding: '3px 10px',
          }}
        >
          {card.category}
        </span>
      </div>
    </div>
  );
}

function HofGrid({
  cards,
  voted,
  onVote,
}: {
  cards: HofCard[];
  voted: Set<string>;
  onVote: (id: string) => void;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 16,
      }}
    >
      {cards.map((card) => (
        <div
          key={card.id}
          style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '20px 20px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.12em',
                color: 'var(--tag)',
                textTransform: 'uppercase',
                lineHeight: 1.4,
              }}
            >
              {card.type}
            </div>
            <VoteButton
              votes={card.votes}
              voted={voted.has(card.id)}
              onVote={() => onVote(card.id)}
            />
          </div>
          <p
            style={{
              fontSize: 13,
              lineHeight: 1.65,
              color: '#c8c4be',
              textWrap: 'pretty',
              flex: 1,
            }}
          >
            "{card.text}"
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 11, color: 'var(--fg-dim)' }}>{card.category}</span>
            <span
              style={{
                fontSize: 10,
                color: '#666',
                border: '1px solid #2a2828',
                borderRadius: 4,
                padding: '2px 8px',
              }}
            >
              {card.category}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function VoteButton({
  votes,
  voted,
  onVote,
  big,
}: {
  votes: number;
  voted: boolean;
  onVote: () => void;
  big?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onVote}
      disabled={voted}
      title={voted ? 'You already voted' : 'Vote up'}
      style={{
        fontFamily: 'inherit',
        fontSize: big ? 13 : 12,
        color: voted ? '#d4a42a' : '#888580',
        background: 'transparent',
        border: `1px solid ${voted ? '#3d2e0a' : '#2a2828'}`,
        borderRadius: 4,
        padding: big ? '4px 10px' : '2px 8px',
        cursor: voted ? 'default' : 'pointer',
        whiteSpace: 'nowrap',
        transition: 'color 0.15s, border-color 0.15s',
      }}
    >
      ▲ {votes.toLocaleString()}
    </button>
  );
}

function LoadingGrid() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 16,
      }}
    >
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="skeleton"
          style={{
            background: 'var(--card-bg)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            height: 180,
            animationDelay: `${i * 0.1}s`,
          }}
        />
      ))}
    </div>
  );
}
